import axios from "../../../../../api/axios";

/**
 * API endpoints for Building Inspection operations
 */
export const ENDPOINTS = {
  BASE: "/v2/building-inspections",
  CREATE: "/v2/building-inspections",
  GET_ALL: "/v2/building-inspections",
  GET_BY_ID: (id) => `/v2/building-inspections/${id}`,
  UPDATE: (id) => `/v2/building-inspections/${id}`,
  DELETE: (id) => `/v2/building-inspections/${id}`,

  SEND_REPORT: "/v2/building-inspections/send-report",

  REQUEST_UPLOAD_URL: (id) => `/v2/building-inspections/${id}/upload-request`,
  CONFIRM_UPLOAD: (id) => `/v2/building-inspections/${id}/confirm-upload`,
  GET_SIGNED_URLS: "/v2/building-inspections/signed-urls",

  // Legacy - should be avoided in new code
  LEGACY_UPLOAD_ATTACHMENTS: (id, section, itemIndex) =>
    `/v2/building-inspections/${id}/${section}/${itemIndex}/attachments`,
};

/**
 * Centralized error handler
 * @param {Error} error - The caught error object
 * @param {string} defaultMessage - Fallback message
 * @throws {Error} with meaningful message
 */
const handleApiError = (error, defaultMessage = "Operation failed") => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    defaultMessage;
  throw new Error(message);
};

/**
 * Create a new Building Inspection (draft or completed – no PDF/email)
 * @param {Object} payload - Inspection data
 * @returns {Promise<Object>} Created inspection document
 */
export const createBuildingInspection = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE, payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to create building inspection");
  }
};

/**
 * Generate PDF report from existing inspection and send email notification
 * @param {string} inspectionId - The _id of the existing inspection
 * @param {string[]} [emails] - Optional custom recipient list
 * @returns {Promise<Object>} Response with sentTo list and success info
 */
export const sendInspectionReport = async (inspectionId, emails = []) => {
  if (!inspectionId) throw new Error("Inspection ID is required");

  try {
    const payload = { _id: inspectionId };
    if (Array.isArray(emails) && emails.length > 0) {
      payload.emails = emails;
    }

    const response = await axios.post(ENDPOINTS.SEND_REPORT, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to send inspection report");
  }
};

/**
 * Get all building inspections (with optional filters)
 * @param {Object} [params={}] - Query params (propertyId, month, year, status, etc.)
 * @returns {Promise<Object>} { success, count, data, ... }
 */
export const getBuildingInspectionList = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ALL, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch building inspections");
  }
};

/**
 * Get single building inspection by ID
 * @param {string} id - Inspection ID
 * @returns {Promise<Object>} Inspection details
 */
export const getBuildingInspectionById = async (id) => {
  if (!id) throw new Error("Inspection ID is required");

  try {
    const response = await axios.get(ENDPOINTS.GET_BY_ID(id));
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspection details");
  }
};

/**
 * Update existing building inspection
 * @param {string} id - Inspection ID
 * @param {Object} payload - Fields to update
 * @returns {Promise<Object>} Updated inspection
 */
export const updateBuildingInspection = async (id, payload) => {
  if (!id) throw new Error("Inspection ID is required");

  try {
    const response = await axios.patch(ENDPOINTS.UPDATE(id), payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to update building inspection");
  }
};

/**
 * Delete building inspection
 * @param {string} id - Inspection ID
 * @returns {Promise<Object>} Success message
 */
export const deleteBuildingInspection = async (id) => {
  if (!id) throw new Error("Inspection ID is required");

  try {
    const response = await axios.delete(ENDPOINTS.DELETE(id));
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete building inspection");
  }
};

/**
 * Request presigned URL for direct client upload to S3
 * @param {string} inspectionId
 * @param {Object} params
 * @param {string} params.section
 * @param {number} params.itemIndex
 * @param {string} params.fileName
 * @param {string} params.contentType
 * @returns {Promise<Object>} { presignedUrl, key, expectedContentType, expiresIn }
 */
export const requestPresignedUploadUrl = async (
  inspectionId,
  { section, itemIndex, fileName, contentType }
) => {
  if (!inspectionId) throw new Error("Inspection ID is required");
  if (!section || itemIndex == null || !fileName || !contentType) {
    throw new Error("Missing required parameters: section, itemIndex, fileName, contentType");
  }

  try {
    const response = await axios.post(ENDPOINTS.REQUEST_UPLOAD_URL(inspectionId), {
      section,
      itemIndex,
      fileName,
      contentType,
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to get presigned upload URL");
  }
};

/**
 * Confirm successful upload and attach S3 key to inspection item
 * @param {string} inspectionId
 * @param {Object} params
 * @param {string} params.section
 * @param {number} params.itemIndex
 * @param {string} params.key
 * @returns {Promise<Object>} Updated attachments list
 */
export const confirmAttachmentUpload = async (
  inspectionId,
  { section, itemIndex, key }
) => {
  if (!inspectionId) throw new Error("Inspection ID is required");
  if (!section || itemIndex == null || !key) {
    throw new Error("Missing required parameters: section, itemIndex, key");
  }

  try {
    const response = await axios.post(ENDPOINTS.CONFIRM_UPLOAD(inspectionId), {
      section,
      itemIndex,
      key,
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to confirm attachment upload");
  }
};

/**
 * Get signed URLs for viewing attachments
 * @param {string[]} keys - Array of S3 object keys
 * @returns {Promise<Record<string, string|null>>} { key: signedUrl | null }
 */
export const getSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) {
    throw new Error("Keys must be a non-empty array");
  }

  try {
    const response = await axios.post(ENDPOINTS.GET_SIGNED_URLS, { keys });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch signed URLs");
  }
};

/**
 * Modern upload flow: request URL → direct S3 upload → confirm
 * @param {string} inspectionId
 * @param {string} section
 * @param {number} itemIndex
 * @param {File} file
 * @param {(progress: number) => void} [onProgress]
 * @returns {Promise<Object>} Confirmation result
 */
export const uploadAttachmentToInspection = async (
  inspectionId,
  section,
  itemIndex,
  file,
  onProgress = () => { }
) => {
  if (!inspectionId || !section || itemIndex == null || !file) {
    throw new Error("Missing required parameters");
  }

  if (!(file instanceof File)) {
    throw new Error("File must be a File object");
  }

  try {
    // 1. Get presigned URL
    const { presignedUrl, key, expectedContentType } = await requestPresignedUploadUrl(
      inspectionId,
      {
        section,
        itemIndex,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
      }
    );

    // 2. Upload directly to S3
    await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": expectedContentType || file.type,
      },
      body: file,
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      },
    });

    // 3. Confirm in backend
    const confirmResult = await confirmAttachmentUpload(inspectionId, {
      section,
      itemIndex,
      key,
    });

    return confirmResult;
  } catch (error) {
    handleApiError(error, "Failed to upload attachment");
  }
};

/**
 * Upload multiple files sequentially to the same item
 * @param {string} inspectionId
 * @param {string} section
 * @param {number} itemIndex
 * @param {File[]} files
 * @param {(file: File, progress: number, index: number) => void} [onFileProgress]
 * @returns {Promise<Array<{file: string, success: boolean, result?: any, error?: string}>>}
 */
export const uploadMultipleAttachments = async (
  inspectionId,
  section,
  itemIndex,
  files,
  onFileProgress = () => { }
) => {
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error("Files must be a non-empty array");
  }

  const results = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    try {
      const result = await uploadAttachmentToInspection(
        inspectionId,
        section,
        itemIndex,
        file,
        (progress) => onFileProgress(file, progress, i)
      );
      results.push({ file: file.name, success: true, result });
    } catch (error) {
      results.push({
        file: file.name,
        success: false,
        error: error.message || "Upload failed",
      });
    }
  }

  return results;
};

/**
 * @deprecated Use uploadAttachmentToInspection or uploadMultipleAttachments instead
 */
export const legacyUploadAttachments = async (inspectionId, section, itemIndex, files) => {
  console.warn("legacyUploadAttachments is deprecated. Use modern presigned flow.");

  try {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];

    fileArray.forEach((file) => {
      if (file instanceof File) {
        formData.append("attachments", file);
      }
    });

    const url = ENDPOINTS.LEGACY_UPLOAD_ATTACHMENTS(inspectionId, section, itemIndex);

    const response = await axios.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return response.data;
  } catch (error) {
    handleApiError(error, "Legacy attachment upload failed");
  }
};